import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Tab,
  Tabs,
  Badge,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Stack,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  CurrencyRupee as MoneyIcon,
  Remove as RemoveIcon,
  AddBox as AddBoxIcon,
  Print as PrintIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { Bill, CreateBillRequest, CreateBillItemRequest, BillSummary, BillItem, UserTodaySummary, MedicineBillingSummary, AdminBillingSummary } from '../types/Bill';
import { Patient } from '../types/Patient';
import { Pharmacy } from '../types/Pharmacy';
import { MedicineReturn, CreateMedicineReturnRequest } from '../types/MedicineReturn';
import { billService } from '../services/billService';
import { patientService } from '../services/patientService';
import { pharmacyService } from '../services/pharmacyService';
import { medicineReturnService } from '../services/medicineReturnService';
import { formatCurrency, formatDate } from '../utils/formatters';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bill-tabpanel-${index}`}
      aria-labelledby={`bill-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const BillingPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const isPharmacy = user?.role === 'Pharmacy';
  const [tabValue, setTabValue] = useState(0);
  const [bills, setBills] = useState<Bill[]>([]);
  const [overdueBills, setOverdueBills] = useState<Bill[]>([]);
  const [summary, setSummary] = useState<BillSummary | null>(null);
  const [myTodaySummary, setMyTodaySummary] = useState<UserTodaySummary | null>(null);
  const [medicineSummary, setMedicineSummary] = useState<MedicineBillingSummary | null>(null);
  const [adminSummary, setAdminSummary] = useState<AdminBillingSummary | null>(null);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [pharmacyItems, setPharmacyItems] = useState<Pharmacy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  
  // Date filter states
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [adminStartDate, setAdminStartDate] = useState<string>('');
  const [adminEndDate, setAdminEndDate] = useState<string>('');
  
  // Medicine Returns state
  const [medicineReturns, setMedicineReturns] = useState<MedicineReturn[]>([]);
  const [openReturnDialog, setOpenReturnDialog] = useState(false);
  const [selectedBillForReturn, setSelectedBillForReturn] = useState<Bill | null>(null);
  const [selectedItemForReturn, setSelectedItemForReturn] = useState<BillItem | null>(null);
  const [returnQuantity, setReturnQuantity] = useState(1);
  const [returnReason, setReturnReason] = useState('');
  const [returnNotes, setReturnNotes] = useState('');
  
  // Form state for creating bills
  const [billItems, setBillItems] = useState<CreateBillItemRequest[]>([{
    itemType: 'Medicine',
    pharmacyId: undefined,
    itemName: '',
    description: '',
    quantity: 1,
    unitPrice: 0,
    discountPercentage: 0,
  }]);
  const [taxAmount, setTaxAmount] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [notes, setNotes] = useState('');
  const [billDate, setBillDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [showDraftPreview, setShowDraftPreview] = useState(false);

  const paymentMethods = ['Cash', 'Credit Card', 'Debit Card', 'Insurance', 'Bank Transfer'];
  const itemTypes = ['Medicine', 'Consultation', 'Dressing', 'Test', 'Procedure', 'Room Charge'];

  useEffect(() => {
    fetchBills();
    fetchOverdueBills();
    fetchSummary();
    fetchPatients();
    fetchPharmacyItems();
    fetchMedicineReturns();
    if (isPharmacy) {
      fetchMyTodaySummary();
      fetchMedicineSummary(selectedDate);
    }
    if (isAdmin) {
      fetchAdminSummary();
    }
  }, [isPharmacy, isAdmin]);

  // Fetch medicine summary when date changes
  useEffect(() => {
    if (isPharmacy && selectedDate) {
      fetchMedicineSummary(selectedDate);
    }
  }, [selectedDate, isPharmacy]);

  // Fetch admin summary when date range changes
  useEffect(() => {
    if (isAdmin) {
      fetchAdminSummary(adminStartDate, adminEndDate);
    }
  }, [adminStartDate, adminEndDate, isAdmin]);

  const fetchBills = async () => {
    try {
      setLoading(true);
      const data = await billService.getAllBills();
      setBills(data);
    } catch (error) {
      console.error('Failed to fetch bills:', error);
      setError('Failed to load bills');
    } finally {
      setLoading(false);
    }
  };

  const fetchOverdueBills = async () => {
    try {
      const data = await billService.getOverdueBills();
      setOverdueBills(data);
    } catch (error) {
      console.error('Failed to fetch overdue bills:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      const data = await billService.getBillSummary();
      setSummary(data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const fetchMyTodaySummary = async () => {
    try {
      const data = await billService.getMyTodaySummary();
      setMyTodaySummary(data);
    } catch (error) {
      console.error('Failed to fetch my today summary:', error);
    }
  };

  const fetchMedicineSummary = async (date: string) => {
    try {
      const data = await billService.getMedicineSummary(date);
      setMedicineSummary(data);
    } catch (error) {
      console.error('Failed to fetch medicine summary:', error);
    }
  };

  const fetchAdminSummary = async (startDate?: string, endDate?: string) => {
    try {
      const data = await billService.getAdminSummary(startDate || undefined, endDate || undefined);
      setAdminSummary(data);
    } catch (error) {
      console.error('Failed to fetch admin summary:', error);
    }
  };

  const fetchMedicineReturns = async () => {
    try {
      const data = await medicineReturnService.getAllReturns();
      setMedicineReturns(data);
    } catch (error) {
      console.error('Failed to fetch medicine returns:', error);
    }
  };

  const fetchPatients = async () => {
    try {
      const data = await patientService.getAllPatients();
      setPatients(data);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
    }
  };

  const fetchPharmacyItems = async () => {
    try {
      const data = await pharmacyService.getAllPharmacyItems();
      setPharmacyItems(data);
    } catch (error) {
      console.error('Failed to fetch pharmacy items:', error);
    }
  };

  const handleOpenDialog = () => {
    setSelectedPatient(null);
    setBillItems([{
      itemType: 'Medicine',
      pharmacyId: undefined,
      itemName: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      discountPercentage: 0,
    }]);
    setTaxAmount(0);
    setDiscountAmount(0);
    setPaymentMethod('');
    setNotes('');
    setBillDate(new Date().toISOString().split('T')[0]);
    setShowDraftPreview(false);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
  };

  const handleAddBillItem = () => {
    setBillItems([...billItems, {
      itemType: 'Medicine',
      pharmacyId: undefined,
      itemName: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      discountPercentage: 0,
    }]);
  };

  // Default GST percentage for Procedure items.
  const DEFAULT_PROCEDURE_GST = 18;

  // Compute the GST amount for a single bill item (only Procedure items contribute).
  const calculateItemGstAmount = (item: CreateBillItemRequest): number => {
    if (item.itemType !== 'Procedure') return 0;
    const lineTotal = (item.quantity || 0) * (item.unitPrice || 0) - (item.discountPercentage || 0);
    const gstPct = item.gstPercentage ?? DEFAULT_PROCEDURE_GST;
    return Math.max(0, lineTotal) * (gstPct / 100);
  };

  // Build a label fragment showing the distinct GST percentages applied to Procedure items.
  const getProcedureGstPercentLabel = (items: CreateBillItemRequest[]): string => {
    const procedureItems = items.filter(
      (item) => item.itemType === 'Procedure' && item.itemName && (item.quantity || 0) > 0
    );
    if (procedureItems.length === 0) return '';
    const uniquePcts = Array.from(
      new Set(procedureItems.map((item) => item.gstPercentage ?? DEFAULT_PROCEDURE_GST))
    ).sort((a, b) => a - b);
    return uniquePcts.map((p) => `${p}%`).join(', ');
  };

  const handleRemoveBillItem = (index: number) => {
    setBillItems(billItems.filter((_, i) => i !== index));
  };

  const handleBillItemChange = (index: number, field: keyof CreateBillItemRequest, value: any) => {
    const updatedItems = [...billItems];
    
    if (field === 'itemType') {
      // When switching item type, reset relevant fields
      updatedItems[index] = {
        ...updatedItems[index],
        itemType: value,
        pharmacyId: undefined,
        itemName: value !== 'Medicine' ? value : '', // Auto-populate itemName with item type for non-Medicine
        unitPrice: 0,
        description: '',
        discountPercentage: 0,
        gstPercentage: value === 'Procedure' ? DEFAULT_PROCEDURE_GST : undefined,
      };
      // Auto-add a Dressing row when Consultation is selected
      if (value === 'Consultation') {
        const hasDressing = updatedItems.some((item, i) => i !== index && item.itemType === 'Dressing');
        if (!hasDressing) {
          updatedItems.push({
            itemType: 'Dressing',
            pharmacyId: undefined,
            itemName: 'Dressing',
            description: '',
            quantity: 1,
            unitPrice: 0,
            discountPercentage: 0,
          });
        }
      }
    } else if (field === 'pharmacyId' && value) {
      const selectedMedicine = pharmacyItems.find(p => p.pharmacyId === value);
      if (selectedMedicine) {
        // Calculate discount amount based on medicine's discount percentage
        const discountAmount = (selectedMedicine.unitPrice * updatedItems[index].quantity * (selectedMedicine.discountPercentage || 0)) / 100;
        updatedItems[index] = {
          ...updatedItems[index],
          pharmacyId: value,
          itemName: selectedMedicine.medicineName,
          unitPrice: selectedMedicine.unitPrice,
          description: selectedMedicine.description,
          discountPercentage: discountAmount, // Auto-populate discount amount
        };
      }
    } else if (field === 'quantity') {
      // Recalculate discount when quantity changes for medicine items
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
      };
      // If it's a medicine item, recalculate discount based on the medicine's discount percentage
      if (updatedItems[index].pharmacyId) {
        const selectedMedicine = pharmacyItems.find(p => p.pharmacyId === updatedItems[index].pharmacyId);
        if (selectedMedicine && selectedMedicine.discountPercentage) {
          const discountAmount = (selectedMedicine.unitPrice * value * selectedMedicine.discountPercentage) / 100;
          updatedItems[index].discountPercentage = discountAmount;
        }
      }
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
      };
    }
    
    setBillItems(updatedItems);
  };

  const calculateSubTotal = () => {
    return billItems.reduce((total, item) => {
      const itemTotal = item.quantity * item.unitPrice;
      const itemDiscount = item.discountPercentage || 0; // Using discountPercentage field as discount amount
      return total + (itemTotal - itemDiscount);
    }, 0);
  };

  const calculateTotal = () => {
    const subTotal = calculateSubTotal();
    const totalGst = billItems.reduce((sum, item) => sum + calculateItemGstAmount(item), 0);
    return subTotal + totalGst + taxAmount - discountAmount;
  };

  const handleSubmit = async () => {
    try {
      if (!selectedPatient) {
        setError('Please select a patient');
        return;
      }

      if (billItems.length === 0 || billItems.some(item => item.quantity <= 0)) {
        setError('Please add valid bill items');
        return;
      }

      // Check if any item has a zero unit price
      const invalidPriceItem = billItems.find(item => item.itemName && item.quantity > 0 && item.unitPrice <= 0);
      if (invalidPriceItem) {
        setError(`Please enter a valid price for "${invalidPriceItem.itemName}"`);
        return;
      }

      const createRequest: CreateBillRequest = {
        patientId: selectedPatient.patientId,
        billAddedDate: billDate || new Date().toISOString().split('T')[0],
        taxAmount,
        discountAmount,
        paymentMethod,
        notes,
        billItems: billItems
          .filter(item => item.itemName && item.quantity > 0)
          .map(item => ({
            ...item,
            // Send GST percentage so backend can persist it; default to 0 for non-Procedure rows.
            gstPercentage: item.itemType === 'Procedure' ? (item.gstPercentage ?? DEFAULT_PROCEDURE_GST) : 0,
          })),
      };

      await billService.createBill(createRequest);
      handleCloseDialog();
      fetchBills();
      fetchOverdueBills();
      fetchSummary();
      if (isPharmacy) {
        fetchMyTodaySummary();
      }
    } catch (error: any) {
      console.error('Failed to create bill:', error);
      setError(error.response?.data?.message || 'Failed to create bill');
    }
  };

  const handleStatusChange = async (billId: number, status: string) => {
    try {
      await billService.updateBillStatus(billId, status);
      fetchBills();
      fetchOverdueBills();
      fetchSummary();
      if (isPharmacy) {
        fetchMyTodaySummary();
      }
    } catch (error: any) {
      console.error('Failed to update bill status:', error);
      setError(error.response?.data?.message || 'Failed to update bill status');
    }
  };

  // Medicine Return Handlers
  const handleOpenReturnDialog = (bill: Bill, item: BillItem) => {
    setSelectedBillForReturn(bill);
    setSelectedItemForReturn(item);
    setReturnQuantity(1);
    setReturnReason('');
    setReturnNotes('');
    setOpenReturnDialog(true);
  };

  const handleCloseReturnDialog = () => {
    setOpenReturnDialog(false);
    setSelectedBillForReturn(null);
    setSelectedItemForReturn(null);
    setReturnQuantity(1);
    setReturnReason('');
    setReturnNotes('');
  };

  const handleSubmitReturn = async () => {
    try {
      if (!selectedBillForReturn || !selectedItemForReturn) {
        setError('Please select a bill and item');
        return;
      }

      if (returnQuantity <= 0 || returnQuantity > selectedItemForReturn.quantity) {
        setError('Invalid return quantity');
        return;
      }

      if (!returnReason.trim()) {
        setError('Please provide a reason for the return');
        return;
      }

      const returnRequest: CreateMedicineReturnRequest = {
        billId: selectedBillForReturn.billId,
        billItemId: selectedItemForReturn.billItemId,
        pharmacyId: selectedItemForReturn.pharmacyId ?? undefined,
        patientId: selectedBillForReturn.patientId,
        medicineName: selectedItemForReturn.itemName,
        quantity: returnQuantity,
        unitPrice: selectedItemForReturn.unitPrice,
        reason: returnReason,
        notes: returnNotes,
      };

      await medicineReturnService.createReturn(returnRequest);
      handleCloseReturnDialog();
      fetchMedicineReturns();
    } catch (error: any) {
      console.error('Failed to create return:', error);
      setError(error.response?.data?.message || 'Failed to create return');
    }
  };

  const handleApproveReturn = async (returnId: number) => {
    try {
      await medicineReturnService.approveReturn(returnId);
      fetchMedicineReturns();
    } catch (error: any) {
      console.error('Failed to approve return:', error);
      setError(error.response?.data?.message || 'Failed to approve return');
    }
  };

  const handleRejectReturn = async (returnId: number) => {
    try {
      await medicineReturnService.rejectReturn(returnId);
      fetchMedicineReturns();
    } catch (error: any) {
      console.error('Failed to reject return:', error);
      setError(error.response?.data?.message || 'Failed to reject return');
    }
  };

  const handleCompleteReturn = async (returnId: number) => {
    try {
      await medicineReturnService.completeReturn(returnId);
      fetchMedicineReturns();
      fetchPharmacyItems(); // Refresh pharmacy stock
    } catch (error: any) {
      console.error('Failed to complete return:', error);
      setError(error.response?.data?.message || 'Failed to complete return');
    }
  };

  const getReturnStatusChip = (status: string) => {
    const statusConfig: { [key: string]: { color: 'warning' | 'info' | 'success' | 'error'; label: string } } = {
      'Pending': { color: 'warning', label: 'Pending' },
      'Approved': { color: 'info', label: 'Approved' },
      'Completed': { color: 'success', label: 'Completed' },
      'Rejected': { color: 'error', label: 'Rejected' },
    };
    const config = statusConfig[status] || { color: 'warning' as const, label: status };
    return <Chip size="small" color={config.color} label={config.label} />;
  };

  const printBill = (bill: Bill) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const currentDate = new Date();
    const timeString = currentDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    const invoiceDate = new Date(bill.billDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });

    // Get patient details from patients list
    const patient = patients.find(p => p.patientId === bill.patientId);
    const patientPhone = patient?.phoneNumber || '';
    const patientAddress = patient ? 
      [patient.address, patient.city, patient.state, patient.zipCode].filter(Boolean).join(', ') : '';

    // Check if bill contains any medicine items
    const hasMedicineItems = bill.billItems?.some(item => item.itemType === 'Medicine') || false;

    // Calculate MRP Total and discount
    const mrpTotal = bill.billItems?.reduce((total, item) => total + (item.unitPrice * item.quantity), 0) || 0;
    const totalDiscount = bill.discountAmount || 0;
    const roundOff = Math.round(bill.totalAmount) - bill.totalAmount;

    // Generate bill items table rows based on bill type
    let billItemsRows: string;
    let tableHeaders: string;

    if (hasMedicineItems) {
      // Medicine bill format with pharmacy data
      tableHeaders = `
        <tr>
          <th style="width: 30px;">SN.</th>
          <th style="width: auto;">PRODUCT NAME</th>
          <th style="width: 50px;">HSN</th>
          <th style="width: 45px;">PACK.</th>
          <th style="width: 55px;">BATCH</th>
          <th style="width: 45px;">EXP.</th>
          <th style="width: 45px;">QTY.</th>
          <th style="width: 55px;">MRP</th>
          <th style="width: 50px;">RATE</th>
          <th style="width: 40px;">DISC</th>
          <th style="width: 60px;">AMOUNT</th>
        </tr>
      `;
      billItemsRows = bill.billItems && bill.billItems.length > 0 
        ? bill.billItems.map((item, index) => {
            const pharmacyItem = item.pharmacyId ? pharmacyItems.find(p => p.pharmacyId === item.pharmacyId) : null;
            const hsnCode = pharmacyItem?.hsnCode || '-';
            const batchNumber = pharmacyItem?.batchNumber || '-';
            const packSize = pharmacyItem?.packSize || '1*10';
            const expiryDate = pharmacyItem?.expiryDate 
              ? new Date(pharmacyItem.expiryDate).toLocaleDateString('en-IN', { month: '2-digit', year: '2-digit' })
              : '-';
            
            return `
            <tr>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: center;">${index + 1}.</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: left;">${item.itemName}</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: center;">${hsnCode}</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: center;">${packSize}</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: center;">${batchNumber}</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: center;">${expiryDate}</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: center;">${item.quantity.toFixed(3)}</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: right;">${item.unitPrice.toFixed(2)}</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: right;">${item.unitPrice.toFixed(2)}</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: right;">${item.discountPercentage.toFixed(2)}</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: right;">${item.totalPrice.toFixed(2)}</td>
            </tr>
          `;
          }).join('')
        : `<tr><td colspan="11" style="padding: 20px; text-align: center; border: 1px solid #000;">No items in this bill</td></tr>`;
    } else {
      // Consultation/Test bill format
      tableHeaders = `
        <tr>
          <th style="width: 30px;">SN.</th>
          <th style="width: auto;">DESCRIPTION</th>
          <th style="width: 80px;">TYPE</th>
          <th style="width: 60px;">QTY</th>
          <th style="width: 80px;">RATE</th>
          <th style="width: 60px;">DISC</th>
          <th style="width: 80px;">AMOUNT</th>
        </tr>
      `;
      billItemsRows = bill.billItems && bill.billItems.length > 0 
        ? bill.billItems.map((item, index) => `
            <tr>
              <td style="padding: 8px 6px; border: 1px solid #000; text-align: center;">${index + 1}.</td>
              <td style="padding: 8px 6px; border: 1px solid #000; text-align: left;">${item.itemName}${item.description ? '<br/><small style="color: #666;">' + item.description + '</small>' : ''}</td>
              <td style="padding: 8px 6px; border: 1px solid #000; text-align: center;">${item.itemType}</td>
              <td style="padding: 8px 6px; border: 1px solid #000; text-align: center;">${item.quantity}</td>
              <td style="padding: 8px 6px; border: 1px solid #000; text-align: right;">${item.unitPrice.toFixed(2)}</td>
              <td style="padding: 8px 6px; border: 1px solid #000; text-align: right;">${item.discountPercentage.toFixed(2)}</td>
              <td style="padding: 8px 6px; border: 1px solid #000; text-align: right;">${item.totalPrice.toFixed(2)}</td>
            </tr>
          `).join('')
        : `<tr><td colspan="7" style="padding: 20px; text-align: center; border: 1px solid #000;">No items in this bill</td></tr>`;
    }

    // Header content based on bill type
    const headerContent = hasMedicineItems ? `
      <div class="store-name">MAITREYI MEDICAL STORE</div>
      <div class="clinic-name">SCULPTE by Plasticare</div>
      <div class="address-line">No 32A/E-74 First Floor, Sculpte By Plasticare,</div>
      <div class="address-line">Kamla Nagar, Agra, Sadar, Uttar Pradesh 282005</div>
      <div class="address-line" style="margin-top: 5px;">Phone : 072360 59392</div>
      <div class="address-line">D.L.No. : RLF20UP2025026474,RLF21UP2025026407</div>
      <div class="address-line">GSTIN : </div>
      <div class="address-line">E-Mail : plasticareaesthetics@gmail.com</div>
    ` : `
      <div class="store-name" style="color: #2C7A9C;">SCULPTE by Plasticare</div>
      <div class="clinic-name">Plastic & Cosmetic Surgery | Hair Transplant</div>
      <div class="address-line" style="margin-top: 5px;">Dr. Puneet Kumar Bharadwaj</div>
      <div class="address-line">MBBS, MS, MCh (Plastic Surgery)</div>
      <div class="address-line" style="margin-top: 5px;">No 32A/E-74 First Floor,</div>
      <div class="address-line">Kamla Nagar, Agra, Sadar, Uttar Pradesh 282005</div>
      <div class="address-line" style="margin-top: 5px;">Phone : 072360 59392</div>
      <div class="address-line">E-Mail : plasticareaesthetics@gmail.com</div>
    `;

    // Invoice title based on bill type
    const invoiceTitle = hasMedicineItems ? 'GST INVOICE' : 'CONSULTATION / SERVICE INVOICE';
    const invoicePrefix = hasMedicineItems ? 'KMS' : 'SCP';

    // Terms based on bill type
    const termsContent = hasMedicineItems ? `
      <div class="terms-title">Terms & Conditions</div>
      <div>1- Medicines can only be returned within 15 days of purchase,</div>
      <div>&nbsp;&nbsp;&nbsp;original bill receipt is mandatory.</div>
      <div>2- Cut/opened medicines, unsealed bottles, or damaged</div>
      <div>&nbsp;&nbsp;&nbsp;packaging will not be accepted for return.</div>
      <div>3- Please verify all medicines at the counter before leaving,</div>
      <div>&nbsp;&nbsp;&nbsp;no responsibility will be accepted thereafter.</div>
    ` : `
      <div class="terms-title">Terms & Conditions</div>
      <div>1- Payment is due at the time of service.</div>
      <div>2- Please keep this receipt for your records.</div>
      <div>3- For any queries, please contact us at the above number.</div>
      <div>4- Follow-up consultations may require separate billing.</div>
    `;

    // Signature section based on bill type
    const signatureFor = hasMedicineItems ? 'For MAITREYI MEDICAL STORE' : 'For SCULPTE by Plasticare';

    const printContent = `
      <html>
        <head>
          <title>${invoiceTitle} - ${bill.billNumber || invoicePrefix + String(bill.billId).padStart(6, '0')}</title>
          <style>
            body {
              font-family: 'Arial', sans-serif;
              margin: 0;
              padding: 10px;
              background-color: #fff;
              color: #000;
              font-size: 11px;
            }
            .main-container {
              border: 2px solid #000;
              padding: 10px;
            }
            .header-container {
              display: flex;
              justify-content: space-between;
              margin-bottom: 5px;
            }
            .left-header {
              width: 58%;
              border-right: 1px solid #000;
              padding-right: 10px;
            }
            .right-header {
              width: 40%;
              padding-left: 10px;
            }
            .store-name {
              font-size: 18px;
              font-weight: bold;
              color: #000;
              margin-bottom: 2px;
            }
            .clinic-name {
              font-size: 13px;
              font-weight: bold;
              margin-bottom: 2px;
            }
            .address-line {
              font-size: 10px;
              margin-bottom: 1px;
            }
            .info-row {
              display: flex;
              margin-bottom: 2px;
              font-size: 10px;
            }
            .info-label {
              font-weight: bold;
              min-width: 90px;
            }
            .info-value {
              flex: 1;
            }
            .gst-invoice-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              border-top: 1px solid #000;
              border-bottom: 1px solid #000;
              padding: 5px 0;
              margin: 5px 0;
            }
            .gst-title {
              font-size: 14px;
              font-weight: bold;
              text-align: center;
              flex: 1;
            }
            .invoice-details {
              text-align: right;
              font-size: 10px;
            }
            .items-table {
              width: 100%;
              border-collapse: collapse;
              margin: 5px 0;
            }
            .items-table th {
              background: #f5f5f5;
              padding: 6px 4px;
              border: 1px solid #000;
              font-weight: bold;
              text-align: center;
              font-size: 10px;
            }
            .items-table td {
              font-size: 10px;
            }
            .footer-section {
              display: flex;
              justify-content: space-between;
              margin-top: 10px;
              border-top: 1px solid #000;
              padding-top: 10px;
            }
            .terms-section {
              width: 55%;
              font-size: 9px;
              border: 1px solid #000;
              padding: 8px;
            }
            .terms-title {
              font-weight: bold;
              text-decoration: underline;
              margin-bottom: 5px;
            }
            .totals-section {
              width: 42%;
            }
            .totals-row {
              display: flex;
              justify-content: space-between;
              padding: 3px 0;
              border-bottom: 1px solid #ddd;
            }
            .totals-row.highlight {
              background: #f5f5f5;
              font-weight: bold;
              border: 1px solid #000;
              padding: 5px;
            }
            .mrp-discount-section {
              border: 1px solid #000;
              padding: 8px;
              margin-bottom: 10px;
              text-align: center;
            }
            .grand-total-section {
              border: 2px solid #000;
              padding: 8px;
              text-align: center;
              font-size: 14px;
              font-weight: bold;
              margin-top: 10px;
            }
            .signature-section {
              display: flex;
              justify-content: flex-end;
              margin-top: 20px;
              padding-top: 10px;
            }
            .signature-box {
              text-align: center;
              width: 200px;
            }
            .signature-line {
              border-top: 1px solid #000;
              margin-top: 40px;
              padding-top: 5px;
              font-size: 10px;
            }
            .amount-words {
              font-size: 10px;
              margin-top: 10px;
              font-style: italic;
            }
            @media print {
              body { margin: 0; padding: 5px; }
              .main-container { border-width: 1px; }
            }
          </style>
        </head>
        <body>
          <div class="main-container">
            <!-- Header Section -->
            <div class="header-container">
              <div class="left-header">
                ${headerContent}
              </div>
              <div class="right-header">
                <div class="info-row">
                  <span class="info-label">Patient Name :</span>
                  <span class="info-value"><strong>${bill.patientName || 'N/A'}</strong></span>
                </div>
                <div class="info-row">
                  <span class="info-label">Patient Address :</span>
                  <span class="info-value">${patientAddress}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Patient Mob :</span>
                  <span class="info-value">${patientPhone}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Dr Name :</span>
                  <span class="info-value">Dr Puneet Bharadwaj</span>
                </div>
              </div>
            </div>

            <!-- Invoice Header -->
            <div class="gst-invoice-header">
              <div style="width: 30%;"></div>
              <div class="gst-title">${invoiceTitle}</div>
              <div class="invoice-details">
                <div>Invoice No. : <strong>${bill.billNumber || invoicePrefix + String(bill.billId).padStart(6, '0')}</strong> Date: ${invoiceDate}</div>
                <div>Issue Time: ${timeString}</div>
              </div>
            </div>

            <!-- Items Table -->
            <table class="items-table">
              <thead>
                ${tableHeaders}
              </thead>
              <tbody>
                ${billItemsRows}
              </tbody>
            </table>

            <!-- Footer Section -->
            <div class="footer-section">
              <!-- Terms & Conditions -->
              <div class="terms-section">
                ${termsContent}
                <div style="margin-top: 10px; font-weight: bold;">
                  Rs. ${numberToWords(Math.round(bill.totalAmount))} Only
                </div>
              </div>

              <!-- Totals Section -->
              <div class="totals-section">
                <div class="mrp-discount-section">
                  <div style="display: flex; justify-content: space-between;">
                    <span><strong>${hasMedicineItems ? 'M.R.P.TOTAL' : 'TOTAL'} :</strong></span>
                    <span><strong>${mrpTotal.toFixed(2)}</strong></span>
                  </div>
                  <div style="display: flex; justify-content: space-between;">
                    <span><strong>YOUR TOTAL DISCOUNT:</strong></span>
                    <span><strong>${totalDiscount.toFixed(2)}</strong></span>
                  </div>
                </div>
                
                <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
                  <tr>
                    <td style="border: 1px solid #000; padding: 5px;">SUB TOTAL</td>
                    <td style="border: 1px solid #000; padding: 5px; text-align: right;">${bill.subTotal.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td style="border: 1px solid #000; padding: 5px;">DISCOUNT %</td>
                    <td style="border: 1px solid #000; padding: 5px; text-align: right;">${mrpTotal > 0 ? ((totalDiscount / mrpTotal) * 100).toFixed(2) : '0.00'}%</td>
                  </tr>
                  <tr>
                    <td style="border: 1px solid #000; padding: 5px;">ROUND OFF</td>
                    <td style="border: 1px solid #000; padding: 5px; text-align: right;">${roundOff.toFixed(2)}</td>
                  </tr>
                </table>

                <div class="grand-total-section">
                  <div style="display: flex; justify-content: space-between;">
                    <span>GRAND TOTAL</span>
                    <span>${Math.round(bill.totalAmount).toFixed(2)}</span>
                  </div>
                </div>

                <div class="signature-section">
                  <div class="signature-box">
                    <div style="margin-bottom: 5px;">${signatureFor}</div>
                    <div class="signature-line">Authorised Signatory</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  // Print draft bill function
  const printDraftBill = () => {
    if (!selectedPatient) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const currentDate = new Date();
    const timeString = currentDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    const invoiceDate = currentDate.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });

    const patientPhone = selectedPatient?.phoneNumber || '';
    const patientAddress = selectedPatient ? 
      [selectedPatient.address, selectedPatient.city, selectedPatient.state, selectedPatient.zipCode].filter(Boolean).join(', ') : '';

    // Check if bill contains any medicine items
    const hasMedicineItems = billItems.some(item => item.itemType === 'Medicine');

    // Filter valid items
    const validItems = billItems.filter(item => item.itemName && item.quantity > 0);

    // Calculate totals
    const mrpTotal = validItems.reduce((total, item) => total + (item.unitPrice * item.quantity), 0);
    const itemsDiscount = validItems.reduce((total, item) => total + (item.discountPercentage || 0), 0);
    const subTotal = mrpTotal - itemsDiscount;
    const totalAmount = subTotal + taxAmount - discountAmount;
    const roundOff = Math.round(totalAmount) - totalAmount;
    const totalDiscount = itemsDiscount + discountAmount;

    // Generate bill items table rows based on bill type
    let billItemsRows: string;
    let tableHeaders: string;

    if (hasMedicineItems) {
      // Medicine bill format with pharmacy data
      tableHeaders = `
        <tr>
          <th style="width: 30px;">SN.</th>
          <th style="width: auto;">PRODUCT NAME</th>
          <th style="width: 50px;">HSN</th>
          <th style="width: 45px;">PACK.</th>
          <th style="width: 55px;">BATCH</th>
          <th style="width: 45px;">EXP.</th>
          <th style="width: 45px;">QTY.</th>
          <th style="width: 55px;">MRP</th>
          <th style="width: 50px;">RATE</th>
          <th style="width: 40px;">DISC</th>
          <th style="width: 60px;">AMOUNT</th>
        </tr>
      `;
      billItemsRows = validItems.length > 0 
        ? validItems.map((item, index) => {
            const pharmacyItem = item.pharmacyId ? pharmacyItems.find(p => p.pharmacyId === item.pharmacyId) : null;
            const hsnCode = pharmacyItem?.hsnCode || '-';
            const batchNumber = pharmacyItem?.batchNumber || '-';
            const packSize = pharmacyItem?.packSize || '1*10';
            const expiryDate = pharmacyItem?.expiryDate 
              ? new Date(pharmacyItem.expiryDate).toLocaleDateString('en-IN', { month: '2-digit', year: '2-digit' })
              : '-';
            const itemTotal = (item.quantity * item.unitPrice) - (item.discountPercentage || 0);
            
            return `
            <tr>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: center;">${index + 1}.</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: left;">${item.itemName}</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: center;">${hsnCode}</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: center;">${packSize}</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: center;">${batchNumber}</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: center;">${expiryDate}</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: center;">${item.quantity.toFixed(3)}</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: right;">${item.unitPrice.toFixed(2)}</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: right;">${item.unitPrice.toFixed(2)}</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: right;">${(item.discountPercentage || 0).toFixed(2)}</td>
              <td style="padding: 6px 4px; border: 1px solid #000; text-align: right;">${itemTotal.toFixed(2)}</td>
            </tr>
          `;
          }).join('')
        : `<tr><td colspan="11" style="padding: 20px; text-align: center; border: 1px solid #000;">No items in this bill</td></tr>`;
    } else {
      // Consultation/Test bill format
      tableHeaders = `
        <tr>
          <th style="width: 30px;">SN.</th>
          <th style="width: auto;">DESCRIPTION</th>
          <th style="width: 80px;">TYPE</th>
          <th style="width: 60px;">QTY</th>
          <th style="width: 80px;">RATE</th>
          <th style="width: 60px;">DISC</th>
          <th style="width: 80px;">AMOUNT</th>
        </tr>
      `;
      billItemsRows = validItems.length > 0 
        ? validItems.map((item, index) => {
            const itemTotal = (item.quantity * item.unitPrice) - (item.discountPercentage || 0);
            return `
            <tr>
              <td style="padding: 8px 6px; border: 1px solid #000; text-align: center;">${index + 1}.</td>
              <td style="padding: 8px 6px; border: 1px solid #000; text-align: left;">${item.itemName}${item.description ? '<br/><small style="color: #666;">' + item.description + '</small>' : ''}</td>
              <td style="padding: 8px 6px; border: 1px solid #000; text-align: center;">${item.itemType}</td>
              <td style="padding: 8px 6px; border: 1px solid #000; text-align: center;">${item.quantity}</td>
              <td style="padding: 8px 6px; border: 1px solid #000; text-align: right;">${item.unitPrice.toFixed(2)}</td>
              <td style="padding: 8px 6px; border: 1px solid #000; text-align: right;">${(item.discountPercentage || 0).toFixed(2)}</td>
              <td style="padding: 8px 6px; border: 1px solid #000; text-align: right;">${itemTotal.toFixed(2)}</td>
            </tr>
          `;
          }).join('')
        : `<tr><td colspan="7" style="padding: 20px; text-align: center; border: 1px solid #000;">No items in this bill</td></tr>`;
    }

    // Header content based on bill type
    const headerContent = hasMedicineItems ? `
      <div class="store-name">MAITREYI MEDICAL STORE</div>
      <div class="clinic-name">SCULPTE by Plasticare</div>
      <div class="address-line">No 32A/E-74 First Floor, Sculpte By Plasticare,</div>
      <div class="address-line">Kamla Nagar, Agra, Sadar, Uttar Pradesh 282005</div>
      <div class="address-line" style="margin-top: 5px;">Phone : 072360 59392</div>
      <div class="address-line">D.L.No. : RLF20UP2025026474,RLF21UP2025026407</div>
      <div class="address-line">GSTIN : </div>
      <div class="address-line">E-Mail : plasticareaesthetics@gmail.com</div>
    ` : `
      <div class="store-name" style="color: #2C7A9C;">SCULPTE by Plasticare</div>
      <div class="clinic-name">Plastic & Cosmetic Surgery | Hair Transplant</div>
      <div class="address-line" style="margin-top: 5px;">Dr. Puneet Kumar Bharadwaj</div>
      <div class="address-line">MBBS, MS, MCh (Plastic Surgery)</div>
      <div class="address-line" style="margin-top: 5px;">No 32A/E-74 First Floor,</div>
      <div class="address-line">Kamla Nagar, Agra, Sadar, Uttar Pradesh 282005</div>
      <div class="address-line" style="margin-top: 5px;">Phone : 072360 59392</div>
      <div class="address-line">E-Mail : plasticareaesthetics@gmail.com</div>
    `;

    // Invoice title based on bill type
    const invoiceTitle = hasMedicineItems ? 'GST INVOICE' : 'CONSULTATION / SERVICE INVOICE';
    const draftBillNumber = hasMedicineItems ? 'KMS-DRAFT' : 'SCP-DRAFT';

    // Terms based on bill type
    const termsContent = hasMedicineItems ? `
      <div class="terms-title">Terms & Conditions</div>
      <div>1- Medicines can only be returned within 15 days of purchase,</div>
      <div>&nbsp;&nbsp;&nbsp;original bill receipt is mandatory.</div>
      <div>2- Cut/opened medicines, unsealed bottles, or damaged</div>
      <div>&nbsp;&nbsp;&nbsp;packaging will not be accepted for return.</div>
      <div>3- Please verify all medicines at the counter before leaving,</div>
      <div>&nbsp;&nbsp;&nbsp;no responsibility will be accepted thereafter.</div>
    ` : `
      <div class="terms-title">Terms & Conditions</div>
      <div>1- Payment is due at the time of service.</div>
      <div>2- Please keep this receipt for your records.</div>
      <div>3- For any queries, please contact us at the above number.</div>
      <div>4- Follow-up consultations may require separate billing.</div>
    `;

    // Signature section based on bill type
    const signatureFor = hasMedicineItems ? 'For MAITREYI MEDICAL STORE' : 'For SCULPTE by Plasticare';

    const printContent = `
      <html>
        <head>
          <title>DRAFT - ${invoiceTitle}</title>
          <style>
            body {
              font-family: 'Arial', sans-serif;
              margin: 0;
              padding: 10px;
              background-color: #fff;
              color: #000;
              font-size: 11px;
              position: relative;
            }
            .draft-watermark {
              position: fixed;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-45deg);
              font-size: 120px;
              font-weight: bold;
              color: rgba(255, 0, 0, 0.15);
              z-index: 1000;
              pointer-events: none;
              white-space: nowrap;
            }
            .main-container {
              border: 2px solid #000;
              padding: 10px;
              position: relative;
              z-index: 1;
            }
            .draft-banner {
              background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
              color: white;
              text-align: center;
              padding: 8px;
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 10px;
              border-radius: 4px;
            }
            .header-container {
              display: flex;
              justify-content: space-between;
              margin-bottom: 5px;
            }
            .left-header {
              width: 58%;
              border-right: 1px solid #000;
              padding-right: 10px;
            }
            .right-header {
              width: 40%;
              padding-left: 10px;
            }
            .store-name {
              font-size: 18px;
              font-weight: bold;
              color: #000;
              margin-bottom: 2px;
            }
            .clinic-name {
              font-size: 13px;
              font-weight: bold;
              margin-bottom: 2px;
            }
            .address-line {
              font-size: 10px;
              margin-bottom: 1px;
            }
            .info-row {
              display: flex;
              margin-bottom: 2px;
              font-size: 10px;
            }
            .info-label {
              font-weight: bold;
              min-width: 90px;
            }
            .info-value {
              flex: 1;
            }
            .gst-invoice-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              border-top: 1px solid #000;
              border-bottom: 1px solid #000;
              padding: 5px 0;
              margin: 5px 0;
            }
            .gst-title {
              font-size: 14px;
              font-weight: bold;
              text-align: center;
              flex: 1;
            }
            .invoice-details {
              text-align: right;
              font-size: 10px;
            }
            .items-table {
              width: 100%;
              border-collapse: collapse;
              margin: 5px 0;
            }
            .items-table th {
              background: #f5f5f5;
              padding: 6px 4px;
              border: 1px solid #000;
              font-weight: bold;
              text-align: center;
              font-size: 10px;
            }
            .items-table td {
              font-size: 10px;
            }
            .footer-section {
              display: flex;
              justify-content: space-between;
              margin-top: 10px;
              border-top: 1px solid #000;
              padding-top: 10px;
            }
            .terms-section {
              width: 55%;
              font-size: 9px;
              border: 1px solid #000;
              padding: 8px;
            }
            .terms-title {
              font-weight: bold;
              text-decoration: underline;
              margin-bottom: 5px;
            }
            .totals-section {
              width: 42%;
            }
            .totals-row {
              display: flex;
              justify-content: space-between;
              padding: 3px 0;
              border-bottom: 1px solid #ddd;
            }
            .totals-row.highlight {
              background: #f5f5f5;
              font-weight: bold;
              border: 1px solid #000;
              padding: 5px;
            }
            .mrp-discount-section {
              border: 1px solid #000;
              padding: 8px;
              margin-bottom: 10px;
              text-align: center;
            }
            .grand-total-section {
              border: 2px solid #000;
              padding: 8px;
              text-align: center;
              font-size: 14px;
              font-weight: bold;
              margin-top: 10px;
            }
            .signature-section {
              display: flex;
              justify-content: flex-end;
              margin-top: 20px;
              padding-top: 10px;
            }
            .signature-box {
              text-align: center;
              width: 200px;
            }
            .signature-line {
              border-top: 1px solid #000;
              margin-top: 40px;
              padding-top: 5px;
              font-size: 10px;
            }
            .amount-words {
              font-size: 10px;
              margin-top: 10px;
              font-style: italic;
            }
            @media print {
              body { margin: 0; padding: 5px; }
              .main-container { border-width: 1px; }
              .draft-watermark {
                position: fixed;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div class="draft-watermark">DRAFT</div>
          <div class="main-container">
            <!-- Draft Banner -->
            <div class="draft-banner">
              ⚠️ DRAFT BILL - NOT A VALID INVOICE ⚠️
            </div>

            <!-- Header Section -->
            <div class="header-container">
              <div class="left-header">
                ${headerContent}
              </div>
              <div class="right-header">
                <div class="info-row">
                  <span class="info-label">Patient Name :</span>
                  <span class="info-value"><strong>${selectedPatient.fullName || 'N/A'}</strong></span>
                </div>
                <div class="info-row">
                  <span class="info-label">Patient Address :</span>
                  <span class="info-value">${patientAddress || 'N/A'}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Patient Mob :</span>
                  <span class="info-value">${patientPhone || 'N/A'}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Dr Name :</span>
                  <span class="info-value">Dr Puneet Bharadwaj</span>
                </div>
              </div>
            </div>

            <!-- Invoice Header -->
            <div class="gst-invoice-header">
              <div style="width: 30%;"></div>
              <div class="gst-title">${invoiceTitle} <span style="color: #d97706; font-size: 12px;">(DRAFT)</span></div>
              <div class="invoice-details">
                <div>Invoice No. : <strong>${draftBillNumber}</strong> Date: ${invoiceDate}</div>
                <div>Issue Time: ${timeString}</div>
              </div>
            </div>

            <!-- Items Table -->
            <table class="items-table">
              <thead>
                ${tableHeaders}
              </thead>
              <tbody>
                ${billItemsRows}
              </tbody>
            </table>

            <!-- Footer Section -->
            <div class="footer-section">
              <!-- Terms & Conditions -->
              <div class="terms-section">
                ${termsContent}
                <div style="margin-top: 10px; font-weight: bold;">
                  Rs. ${numberToWords(Math.round(totalAmount))} Only
                </div>
              </div>

              <!-- Totals Section -->
              <div class="totals-section">
                <div class="mrp-discount-section">
                  <div style="display: flex; justify-content: space-between;">
                    <span><strong>${hasMedicineItems ? 'M.R.P.TOTAL' : 'TOTAL'} :</strong></span>
                    <span><strong>${mrpTotal.toFixed(2)}</strong></span>
                  </div>
                  <div style="display: flex; justify-content: space-between;">
                    <span><strong>YOUR TOTAL DISCOUNT:</strong></span>
                    <span><strong>${totalDiscount.toFixed(2)}</strong></span>
                  </div>
                </div>
                
                <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
                  <tr>
                    <td style="border: 1px solid #000; padding: 5px;">SUB TOTAL</td>
                    <td style="border: 1px solid #000; padding: 5px; text-align: right;">${subTotal.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td style="border: 1px solid #000; padding: 5px;">TAX AMOUNT</td>
                    <td style="border: 1px solid #000; padding: 5px; text-align: right;">${taxAmount.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td style="border: 1px solid #000; padding: 5px;">DISCOUNT</td>
                    <td style="border: 1px solid #000; padding: 5px; text-align: right;">${discountAmount.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td style="border: 1px solid #000; padding: 5px;">ROUND OFF</td>
                    <td style="border: 1px solid #000; padding: 5px; text-align: right;">${roundOff.toFixed(2)}</td>
                  </tr>
                </table>

                <div class="grand-total-section">
                  <div style="display: flex; justify-content: space-between;">
                    <span>GRAND TOTAL</span>
                    <span>${Math.round(totalAmount).toFixed(2)}</span>
                  </div>
                </div>

                <div class="signature-section">
                  <div class="signature-box">
                    <div style="margin-bottom: 5px;">${signatureFor}</div>
                    <div class="signature-line">Authorised Signatory</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  // Helper function to convert number to words
  const numberToWords = (num: number): string => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    if (num === 0) return 'Zero';
    if (num < 0) return 'Minus ' + numberToWords(-num);
    
    let words = '';
    
    if (Math.floor(num / 10000000) > 0) {
      words += numberToWords(Math.floor(num / 10000000)) + ' Crore ';
      num %= 10000000;
    }
    
    if (Math.floor(num / 100000) > 0) {
      words += numberToWords(Math.floor(num / 100000)) + ' Lakh ';
      num %= 100000;
    }
    
    if (Math.floor(num / 1000) > 0) {
      words += numberToWords(Math.floor(num / 1000)) + ' Thousand ';
      num %= 1000;
    }
    
    if (Math.floor(num / 100) > 0) {
      words += numberToWords(Math.floor(num / 100)) + ' Hundred ';
      num %= 100;
    }
    
    if (num > 0) {
      if (num < 20) {
        words += ones[num];
      } else {
        words += tens[Math.floor(num / 10)];
        if (num % 10 > 0) {
          words += ' ' + ones[num % 10];
        }
      }
    }
    
    return words.trim();
  };

  // Render draft bill preview
  const renderDraftPreview = () => {
    if (!selectedPatient || billItems.length === 0) {
      return (
        <Box sx={{ p: 3, textAlign: 'center', color: '#6b7280' }}>
          <Typography>Please select a patient and add items to preview the bill</Typography>
        </Box>
      );
    }

    const currentDate = new Date();
    const timeString = currentDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    const invoiceDate = currentDate.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });

    const patientPhone = selectedPatient?.phoneNumber || '';
    const patientAddress = selectedPatient ? 
      [selectedPatient.address, selectedPatient.city, selectedPatient.state, selectedPatient.zipCode].filter(Boolean).join(', ') : '';

    // Check if bill contains any medicine items
    const hasMedicineItems = billItems.some(item => item.itemType === 'Medicine');

    // Calculate totals
    const validItems = billItems.filter(item => item.itemName && item.quantity > 0);
    const mrpTotal = validItems.reduce((total, item) => total + (item.unitPrice * item.quantity), 0);
    const itemsDiscount = validItems.reduce((total, item) => total + (item.discountPercentage || 0), 0);
    const subTotal = mrpTotal - itemsDiscount;
    const procedureGstTotal = validItems.reduce((total, item) => total + calculateItemGstAmount(item), 0);
    const totalAmount = subTotal + procedureGstTotal + taxAmount - discountAmount;
    const roundOff = Math.round(totalAmount) - totalAmount;

    // Draft Bill Number
    const draftBillNumber = hasMedicineItems ? 'KMS-DRAFT' : 'SCP-DRAFT';

    return (
      <Box sx={{ 
        border: '2px solid #000', 
        p: 2, 
        backgroundColor: '#fff',
        fontFamily: 'Arial, sans-serif',
        fontSize: '11px',
        maxHeight: '600px',
        overflow: 'auto'
      }}>
        {/* Header Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, pb: 1, borderBottom: '1px solid #000' }}>
          <Box sx={{ width: '58%', pr: 1, borderRight: '1px solid #000' }}>
            {hasMedicineItems ? (
              <>
                <Typography sx={{ fontSize: '18px', fontWeight: 'bold', mb: 0.5 }}>MAITREYI MEDICAL STORE</Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 'bold', mb: 0.5 }}>SCULPTE by Plasticare</Typography>
                <Typography sx={{ fontSize: '10px' }}>No 32A/E-74 First Floor, Sculpte By Plasticare,</Typography>
                <Typography sx={{ fontSize: '10px' }}>Kamla Nagar, Agra, Sadar, Uttar Pradesh 282005</Typography>
                <Typography sx={{ fontSize: '10px', mt: 0.5 }}>Phone : 072360 59392</Typography>
                <Typography sx={{ fontSize: '10px' }}>D.L.No. : RLF20UP2025026474,RLF21UP2025026407</Typography>
                <Typography sx={{ fontSize: '10px' }}>E-Mail : plasticareaesthetics@gmail.com</Typography>
              </>
            ) : (
              <>
                <Typography sx={{ fontSize: '18px', fontWeight: 'bold', color: '#2C7A9C', mb: 0.5 }}>SCULPTE by Plasticare</Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 'bold', mb: 0.5 }}>Plastic & Cosmetic Surgery | Hair Transplant</Typography>
                <Typography sx={{ fontSize: '10px', mt: 0.5 }}>Dr. Puneet Kumar Bharadwaj</Typography>
                <Typography sx={{ fontSize: '10px' }}>MBBS, MS, MCh (Plastic Surgery)</Typography>
                <Typography sx={{ fontSize: '10px', mt: 0.5 }}>No 32A/E-74 First Floor,</Typography>
                <Typography sx={{ fontSize: '10px' }}>Kamla Nagar, Agra, Sadar, Uttar Pradesh 282005</Typography>
                <Typography sx={{ fontSize: '10px', mt: 0.5 }}>Phone : 072360 59392</Typography>
                <Typography sx={{ fontSize: '10px' }}>E-Mail : plasticareaesthetics@gmail.com</Typography>
              </>
            )}
          </Box>
          <Box sx={{ width: '40%', pl: 1 }}>
            <Box sx={{ display: 'flex', mb: 0.5, fontSize: '10px' }}>
              <Typography sx={{ fontWeight: 'bold', minWidth: '90px', fontSize: '10px' }}>Patient Name :</Typography>
              <Typography sx={{ fontWeight: 'bold', fontSize: '10px' }}>{selectedPatient.fullName}</Typography>
            </Box>
            <Box sx={{ display: 'flex', mb: 0.5, fontSize: '10px' }}>
              <Typography sx={{ fontWeight: 'bold', minWidth: '90px', fontSize: '10px' }}>Patient Address :</Typography>
              <Typography sx={{ fontSize: '10px' }}>{patientAddress || 'N/A'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', mb: 0.5, fontSize: '10px' }}>
              <Typography sx={{ fontWeight: 'bold', minWidth: '90px', fontSize: '10px' }}>Patient Mob :</Typography>
              <Typography sx={{ fontSize: '10px' }}>{patientPhone || 'N/A'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', mb: 0.5, fontSize: '10px' }}>
              <Typography sx={{ fontWeight: 'bold', minWidth: '90px', fontSize: '10px' }}>Dr Name :</Typography>
              <Typography sx={{ fontSize: '10px' }}>Dr Puneet Bharadwaj</Typography>
            </Box>
          </Box>
        </Box>

        {/* Invoice Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          borderTop: '1px solid #000', 
          borderBottom: '1px solid #000', 
          py: 0.5, 
          my: 1 
        }}>
          <Box sx={{ width: '30%' }}></Box>
          <Typography sx={{ fontSize: '14px', fontWeight: 'bold', textAlign: 'center', flex: 1 }}>
            {hasMedicineItems ? 'GST INVOICE' : 'CONSULTATION / SERVICE INVOICE'}
            <Chip label="DRAFT" size="small" color="warning" sx={{ ml: 1, fontSize: '10px' }} />
          </Typography>
          <Box sx={{ textAlign: 'right', fontSize: '10px' }}>
            <Typography sx={{ fontSize: '10px' }}>Invoice No. : <strong>{draftBillNumber}</strong> Date: {invoiceDate}</Typography>
            <Typography sx={{ fontSize: '10px' }}>Issue Time: {timeString}</Typography>
          </Box>
        </Box>

        {/* Items Table */}
        <TableContainer component={Paper} elevation={0} sx={{ my: 1 }}>
          <Table size="small" sx={{ '& th, & td': { border: '1px solid #000', fontSize: '10px', p: 0.5 } }}>
            <TableHead>
              {hasMedicineItems ? (
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '30px' }}>SN.</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>PRODUCT NAME</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '50px' }}>HSN</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '45px' }}>PACK.</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '55px' }}>BATCH</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '45px' }}>EXP.</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '45px' }}>QTY.</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'right', width: '55px' }}>MRP</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'right', width: '50px' }}>RATE</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'right', width: '40px' }}>DISC</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'right', width: '60px' }}>AMOUNT</TableCell>
                </TableRow>
              ) : (
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '30px' }}>SN.</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>DESCRIPTION</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '80px' }}>TYPE</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '60px' }}>QTY</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'right', width: '80px' }}>RATE</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'right', width: '60px' }}>DISC</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'right', width: '80px' }}>AMOUNT</TableCell>
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {validItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={hasMedicineItems ? 11 : 7} sx={{ textAlign: 'center', py: 2 }}>
                    No items added yet
                  </TableCell>
                </TableRow>
              ) : (
                validItems.map((item, index) => {
                  const pharmacyItem = item.pharmacyId ? pharmacyItems.find(p => p.pharmacyId === item.pharmacyId) : null;
                  const itemTotal = (item.quantity * item.unitPrice) - (item.discountPercentage || 0);
                  
                  if (hasMedicineItems) {
                    const hsnCode = pharmacyItem?.hsnCode || '-';
                    const batchNumber = pharmacyItem?.batchNumber || '-';
                    const packSize = pharmacyItem?.packSize || '1*10';
                    const expiryDate = pharmacyItem?.expiryDate 
                      ? new Date(pharmacyItem.expiryDate).toLocaleDateString('en-IN', { month: '2-digit', year: '2-digit' })
                      : '-';
                    
                    return (
                      <TableRow key={index}>
                        <TableCell sx={{ textAlign: 'center' }}>{index + 1}.</TableCell>
                        <TableCell>{item.itemName}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>{hsnCode}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>{packSize}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>{batchNumber}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>{expiryDate}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>{item.quantity.toFixed(3)}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{item.unitPrice.toFixed(2)}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{item.unitPrice.toFixed(2)}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{(item.discountPercentage || 0).toFixed(2)}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{itemTotal.toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  } else {
                    return (
                      <TableRow key={index}>
                        <TableCell sx={{ textAlign: 'center' }}>{index + 1}.</TableCell>
                        <TableCell>
                          {item.itemName}
                          {item.description && <Typography variant="caption" sx={{ display: 'block', color: '#666' }}>{item.description}</Typography>}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>{item.itemType}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>{item.quantity}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{item.unitPrice.toFixed(2)}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{(item.discountPercentage || 0).toFixed(2)}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{itemTotal.toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  }
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Footer Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 1, borderTop: '1px solid #000' }}>
          {/* Terms & Conditions */}
          <Box sx={{ width: '55%', border: '1px solid #000', p: 1, fontSize: '9px' }}>
            <Typography sx={{ fontWeight: 'bold', textDecoration: 'underline', mb: 0.5, fontSize: '9px' }}>
              Terms & Conditions
            </Typography>
            {hasMedicineItems ? (
              <>
                <Typography sx={{ fontSize: '9px' }}>1- Medicines can only be returned within 15 days of purchase,</Typography>
                <Typography sx={{ fontSize: '9px' }}>&nbsp;&nbsp;&nbsp;original bill receipt is mandatory.</Typography>
                <Typography sx={{ fontSize: '9px' }}>2- Cut/opened medicines, unsealed bottles, or damaged</Typography>
                <Typography sx={{ fontSize: '9px' }}>&nbsp;&nbsp;&nbsp;packaging will not be accepted for return.</Typography>
                <Typography sx={{ fontSize: '9px' }}>3- Please verify all medicines at the counter before leaving,</Typography>
                <Typography sx={{ fontSize: '9px' }}>&nbsp;&nbsp;&nbsp;no responsibility will be accepted thereafter.</Typography>
              </>
            ) : (
              <>
                <Typography sx={{ fontSize: '9px' }}>1- Payment is due at the time of service.</Typography>
                <Typography sx={{ fontSize: '9px' }}>2- Please keep this receipt for your records.</Typography>
                <Typography sx={{ fontSize: '9px' }}>3- For any queries, please contact us at the above number.</Typography>
                <Typography sx={{ fontSize: '9px' }}>4- Follow-up consultations may require separate billing.</Typography>
              </>
            )}
            <Typography sx={{ mt: 1, fontWeight: 'bold', fontSize: '10px' }}>
              Rs. {numberToWords(Math.round(totalAmount))} Only
            </Typography>
          </Box>

          {/* Totals Section */}
          <Box sx={{ width: '42%' }}>
            <Box sx={{ border: '1px solid #000', p: 1, mb: 1, textAlign: 'center' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: 'bold', fontSize: '10px' }}>{hasMedicineItems ? 'M.R.P.TOTAL' : 'TOTAL'} :</Typography>
                <Typography sx={{ fontWeight: 'bold', fontSize: '10px' }}>{mrpTotal.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: 'bold', fontSize: '10px' }}>YOUR TOTAL DISCOUNT:</Typography>
                <Typography sx={{ fontWeight: 'bold', fontSize: '10px' }}>{(itemsDiscount + discountAmount).toFixed(2)}</Typography>
              </Box>
            </Box>
            
            <Table size="small" sx={{ '& th, & td': { border: '1px solid #000', fontSize: '11px', p: 0.5 } }}>
              <TableBody>
                <TableRow>
                  <TableCell>SUB TOTAL</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>{subTotal.toFixed(2)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    GST (PROCEDURES)
                    {getProcedureGstPercentLabel(validItems) && ` @ ${getProcedureGstPercentLabel(validItems)}`}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>{procedureGstTotal.toFixed(2)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>TAX AMOUNT</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>{taxAmount.toFixed(2)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>DISCOUNT</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>{discountAmount.toFixed(2)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>ROUND OFF</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>{roundOff.toFixed(2)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Box sx={{ border: '2px solid #000', p: 1, textAlign: 'center', fontSize: '14px', fontWeight: 'bold', mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: 'bold', fontSize: '14px' }}>GRAND TOTAL</Typography>
                <Typography sx={{ fontWeight: 'bold', fontSize: '14px' }}>{Math.round(totalAmount).toFixed(2)}</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, pt: 1 }}>
              <Box sx={{ textAlign: 'center', width: '200px' }}>
                <Typography sx={{ fontSize: '10px', mb: 0.5 }}>
                  {hasMedicineItems ? 'For MAITREYI MEDICAL STORE' : 'For SCULPTE by Plasticare'}
                </Typography>
                <Box sx={{ borderTop: '1px solid #000', mt: 4, pt: 0.5 }}>
                  <Typography sx={{ fontSize: '10px' }}>Authorised Signatory</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusChip = (status: string) => {
    const colors: { [key: string]: 'success' | 'warning' | 'error' | 'default' } = {
      'Paid': 'success',
      'Pending': 'warning',
      'Overdue': 'error',
      'Cancelled': 'default',
    };
    return <Chip label={status} color={colors[status] || 'default'} size="small" />;
  };

  const renderBillTable = (billList: Bill[]) => (
    <TableContainer 
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
      }}
    >
      <Table>
        <TableHead>
          <TableRow sx={{ background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)' }}>
            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Bill Number</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Patient</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Bill Date</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Due Date</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Total Amount</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Status</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {billList.map((bill, index) => (
            <TableRow 
              key={bill.billId}
              sx={{
                backgroundColor: index % 2 === 0 ? '#f8fafc' : 'white',
                '&:hover': {
                  backgroundColor: '#f0f9ff',
                },
              }}
            >
              <TableCell sx={{ py: 2 }}>
                <Typography variant="body2" fontWeight="bold" sx={{ color: '#1E5A73' }}>
                  {bill.billNumber}
                </Typography>
              </TableCell>
              <TableCell sx={{ py: 2, color: '#374151' }}>{bill.patientName}</TableCell>
              <TableCell sx={{ py: 2, color: '#374151' }}>{formatDate(bill.billAddedDate ?? bill.billDate)}</TableCell>
              <TableCell sx={{ py: 2, color: '#374151' }}>{formatDate(bill.dueDate)}</TableCell>
              <TableCell sx={{ py: 2 }}>
                <Typography variant="body2" fontWeight="bold" sx={{ color: '#059669' }}>
                  {formatCurrency(bill.totalAmount)}
                </Typography>
              </TableCell>
              <TableCell sx={{ py: 2 }}>{getStatusChip(bill.status)}</TableCell>
              <TableCell sx={{ py: 2 }}>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Print Invoice">
                    <IconButton
                      size="small"
                      onClick={() => printBill(bill)}
                      sx={{
                        color: '#6366f1',
                        '&:hover': {
                          backgroundColor: '#f0f4ff',
                        }
                      }}
                    >
                      <PrintIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {bill.billItems && bill.billItems.length > 0 && (
                    <Tooltip title="Return Item">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedBillForReturn(bill);
                          setOpenReturnDialog(true);
                        }}
                        sx={{
                          color: '#f59e0b',
                          '&:hover': {
                            backgroundColor: '#fef3c7',
                          }
                        }}
                      >
                        <UndoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {bill.status === 'Pending' && (
                    <Button
                      size="small"
                      variant="contained"
                      sx={{
                        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 2,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #047857 0%, #065f46 100%)',
                        },
                      }}
                      onClick={() => handleStatusChange(bill.billId, 'Paid')}
                    >
                      Mark Paid
                    </Button>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        sx={{ 
          background: 'linear-gradient(135deg, #e8f4f8 0%, #c5dfe8 100%)',
          borderRadius: 3,
          m: 2
        }}
      >
        <CircularProgress sx={{ color: '#2C7A9C' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3, 
      minHeight: '100vh', 
      background: 'transparent',
    }}>
      <Card 
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
          color: 'white',
          borderRadius: 4,
          mb: 3,
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ReceiptIcon sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                  Billing Management
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, color: 'white' }}>
                  Manage patient bills, payments, and invoices
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.3)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)',
                }
              }}
            >
              Create Bill
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards - Only visible to Admin with Date Range Filter */}
      {isAdmin && (
        <>
          {/* Admin Date Range Filter */}
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', mb: 3, p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E5A73' }}>
                  📊 Admin Billing Summary
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={adminStartDate}
                  onChange={(e) => setAdminStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={adminEndDate}
                  onChange={(e) => setAdminEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  onClick={() => { setAdminStartDate(''); setAdminEndDate(''); }}
                  sx={{ borderColor: '#2C7A9C', color: '#2C7A9C' }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </Card>

          {adminSummary && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card 
                  elevation={0}
                  sx={{
                    background: 'linear-gradient(135deg, #e8f4f8 0%, #c5dfe8 100%)',
                    border: '1px solid #6AB5D1',
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ p: 2, textAlign: 'center' }}>
                    <ReceiptIcon sx={{ fontSize: 36, color: '#2C7A9C', mb: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E5A73', mb: 1 }}>
                      {adminSummary.totalBills}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#4a6a6a' }}>
                      Total Bills
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card 
                  elevation={0}
                  sx={{
                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                    border: '1px solid #4ade80',
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ p: 2, textAlign: 'center' }}>
                    <MoneyIcon sx={{ fontSize: 36, color: '#16a34a', mb: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#15803d', mb: 1 }}>
                      {formatCurrency(adminSummary.totalBillAmount)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Total Bill Amount
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card 
                  elevation={0}
                  sx={{
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    border: '1px solid #fbbf24',
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ p: 2, textAlign: 'center' }}>
                    <MoneyIcon sx={{ fontSize: 36, color: '#d97706', mb: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#92400e', mb: 1 }}>
                      {formatCurrency(adminSummary.totalMedicineCostPrice)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Medicine Cost Price
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card 
                  elevation={0}
                  sx={{
                    background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                    border: '1px solid #86efac',
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ p: 2, textAlign: 'center' }}>
                    <PaymentIcon sx={{ fontSize: 36, color: '#059669', mb: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#047857', mb: 1 }}>
                      {formatCurrency(adminSummary.totalMedicineProfit)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Medicine Profit
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card 
                  elevation={0}
                  sx={{
                    background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                    border: '1px solid #f87171',
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ p: 2, textAlign: 'center' }}>
                    <WarningIcon sx={{ fontSize: 36, color: '#dc2626', mb: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#991b1b', mb: 1 }}>
                      {formatCurrency(adminSummary.totalPendingAmount)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Pending Amount
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </>
      )}

      {/* Today's Summary Cards - Visible to Pharmacy users with Date Filter */}
      {isPharmacy && (
        <>
          {/* Date Filter for Pharmacy */}
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', mb: 3, p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E5A73' }}>
                  💊 Medicine Billing Summary (Only Medicine Bills)
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Select Date"
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  variant="outlined"
                  onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
                  sx={{ borderColor: '#2C7A9C', color: '#2C7A9C' }}
                >
                  Today
                </Button>
              </Grid>
            </Grid>
          </Card>

          {medicineSummary && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  elevation={0}
                  sx={{
                    background: 'linear-gradient(135deg, #e8f4f8 0%, #c5dfe8 100%)',
                    border: '1px solid #6AB5D1',
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <ReceiptIcon sx={{ fontSize: 40, color: '#2C7A9C', mb: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1E5A73', mb: 1 }}>
                      {medicineSummary.totalBills}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#4a6a6a' }}>
                      Bills ({new Date(selectedDate).toLocaleDateString()})
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  elevation={0}
                  sx={{
                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                    border: '1px solid #4ade80',
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <MoneyIcon sx={{ fontSize: 40, color: '#16a34a', mb: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#15803d', mb: 1 }}>
                      {formatCurrency(medicineSummary.totalBillAmount)}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#64748b' }}>
                      Total Billed
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  elevation={0}
                  sx={{
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    border: '1px solid #fbbf24',
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <MoneyIcon sx={{ fontSize: 40, color: '#d97706', mb: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#92400e', mb: 1 }}>
                      {formatCurrency(medicineSummary.totalCostPrice)}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#64748b' }}>
                      Cumulative Cost Price
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  elevation={0}
                  sx={{
                    background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                    border: '1px solid #86efac',
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <PaymentIcon sx={{ fontSize: 40, color: '#059669', mb: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#047857', mb: 1 }}>
                      {formatCurrency(medicineSummary.totalProfit)}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#64748b' }}>
                      Profit
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </>
      )}

      {/* Search Box */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', mb: 3, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Search by bill number, patient name, or status..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: '#64748b', mr: 1 }} />,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover fieldset': { borderColor: '#2C7A9C' },
              '&.Mui-focused fieldset': { borderColor: '#2C7A9C' },
            },
          }}
        />
      </Card>

      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRadius: 2,
          p: 2,
          mb: 3,
          border: '1px solid #e2e8f0',
        }}
      >
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{
            '& .MuiTab-root': {
              fontWeight: 600,
              textTransform: 'none',
              color: '#64748b',
              '&.Mui-selected': {
                color: '#2C7A9C',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#2C7A9C',
              height: 3,
            },
          }}
        >
          <Tab label="All Bills" />
          <Tab 
            label={
              <Badge badgeContent={overdueBills.length} color="error">
                Overdue Bills
              </Badge>
            }
          />
          <Tab 
            label={
              <Badge badgeContent={medicineReturns.filter(r => r.status === 'Pending').length} color="warning">
                Medicine Returns
              </Badge>
            }
          />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {renderBillTable(bills.filter(bill => 
          bill.billNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
          bill.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          bill.status.toLowerCase().includes(searchTerm.toLowerCase())
        ))}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Typography variant="h6" gutterBottom color="error.main">
          <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Overdue Bills
        </Typography>
        {renderBillTable(overdueBills.filter(bill => 
          bill.billNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
          bill.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          bill.status.toLowerCase().includes(searchTerm.toLowerCase())
        ))}
      </TabPanel>

      {/* Medicine Returns Tab */}
      <TabPanel value={tabValue} index={2}>
        <Typography variant="h6" gutterBottom sx={{ color: '#2C7A9C' }}>
          <UndoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Medicine Returns
        </Typography>
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)' }}>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Return ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Patient</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Medicine</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Qty</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Refund</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Reason</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Date</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {medicineReturns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#6b7280' }}>
                    No medicine returns found
                  </TableCell>
                </TableRow>
              ) : (
                medicineReturns.map((ret, index) => (
                  <TableRow 
                    key={ret.returnId}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#f8fafc' : 'white',
                      '&:hover': { backgroundColor: '#f0f9ff' },
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: '#1E5A73' }}>
                        #{ret.returnId}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2, color: '#374151' }}>{ret.patientName || 'N/A'}</TableCell>
                    <TableCell sx={{ py: 2, color: '#374151' }}>{ret.medicineName}</TableCell>
                    <TableCell sx={{ py: 2, color: '#374151' }}>{ret.quantity}</TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: '#059669' }}>
                        {formatCurrency(ret.totalRefund)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2, color: '#374151' }}>{ret.reason}</TableCell>
                    <TableCell sx={{ py: 2, color: '#374151' }}>{formatDate(ret.returnDate)}</TableCell>
                    <TableCell sx={{ py: 2 }}>{getReturnStatusChip(ret.status)}</TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Stack direction="row" spacing={1}>
                        {ret.status === 'Pending' && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                              onClick={() => handleApproveReturn(ret.returnId)}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                              onClick={() => handleRejectReturn(ret.returnId)}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        {ret.status === 'Approved' && (
                          <Button
                            size="small"
                            variant="contained"
                            sx={{
                              background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
                              color: 'white',
                              textTransform: 'none',
                              fontWeight: 600,
                              borderRadius: 2,
                            }}
                            onClick={() => handleCompleteReturn(ret.returnId)}
                          >
                            Complete
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Create Bill Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
          color: 'white',
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Create New Bill</Typography>
          <Button
            variant="outlined"
            startIcon={showDraftPreview ? <VisibilityOffIcon /> : <VisibilityIcon />}
            onClick={() => setShowDraftPreview(!showDraftPreview)}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.5)',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            {showDraftPreview ? 'Hide Preview' : 'Show Preview'}
          </Button>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Grid container>
            {/* Form Section */}
            <Grid item xs={12} md={showDraftPreview ? 6 : 12}>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  {/* Patient Selection */}
                  <Grid item xs={12} sm={8}>
                    <Autocomplete
                      options={patients}
                      getOptionLabel={(option) => `${option.fullName} - ${option.email}`}
                      value={selectedPatient}
                      onChange={(event, newValue) => setSelectedPatient(newValue)}
                      renderInput={(params) => (
                        <TextField {...params} label="Select Patient" />
                      )}
                    />
                  </Grid>

                  {/* Bill Date */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Bill Date"
                      type="date"
                      value={billDate}
                      onChange={(e) => setBillDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

            {/* Bill Items Table */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Bill Items
                </Typography>
                <Button
                  startIcon={<AddBoxIcon />}
                  variant="outlined"
                  onClick={handleAddBillItem}
                  sx={{
                    borderColor: '#2C7A9C',
                    color: '#2C7A9C',
                    '&:hover': {
                      borderColor: '#1C5D7D',
                      backgroundColor: 'rgba(44, 122, 156, 0.1)',
                    },
                  }}
                >
                  Add Item
                </Button>
              </Box>
              
              <TableContainer component={Paper} elevation={2} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 'bold', width: '12%' }}>
                        Item Type
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 'bold', width: '18%' }}>
                        Item Name
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 'bold', width: '16%' }}>
                        Description
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 'bold', width: '7%' }}>
                        Qty
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 'bold', width: '11%' }}>
                        Unit Price
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 'bold', width: '9%' }}>
                        Discount
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 'bold', width: '8%' }}>
                        GST %
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 'bold', width: '12%' }}>
                        Total
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#f8fafc', fontWeight: 'bold', width: '5%' }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {billItems.map((item, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={item.itemType}
                              onChange={(e) => handleBillItemChange(index, 'itemType', e.target.value)}
                              sx={{ minHeight: 40 }}
                            >
                              {itemTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                  {type}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          {item.itemType === 'Medicine' ? (
                            <Autocomplete
                              size="small"
                              options={pharmacyItems}
                              getOptionLabel={(option) => option.medicineName}
                              onChange={(event, newValue) => {
                                if (newValue) {
                                  handleBillItemChange(index, 'pharmacyId', newValue.pharmacyId);
                                } else {
                                  // Clear the medicine selection
                                  const updatedItems = [...billItems];
                                  updatedItems[index] = {
                                    ...updatedItems[index],
                                    pharmacyId: undefined,
                                    itemName: '',
                                    unitPrice: 0,
                                    description: '',
                                  };
                                  setBillItems(updatedItems);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField {...params} placeholder="Select Medicine" />
                              )}
                            />
                          ) : (
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="Enter item name"
                              value={item.itemName}
                              onChange={(e) => handleBillItemChange(index, 'itemName', e.target.value)}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Description"
                            value={item.description}
                            onChange={(e) => handleBillItemChange(index, 'description', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Qty"
                            value={item.quantity === 0 ? '' : item.quantity}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^0-9]/g, '');
                              handleBillItemChange(index, 'quantity', val === '' ? 0 : parseInt(val));
                            }}
                            onBlur={(e) => {
                              if (e.target.value === '' || parseInt(e.target.value) < 1) {
                                handleBillItemChange(index, 'quantity', 1);
                              }
                            }}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', style: { textAlign: 'center' } }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Price"
                            value={item.unitPrice === 0 ? '' : item.unitPrice}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^0-9.]/g, '');
                              handleBillItemChange(index, 'unitPrice', val === '' ? 0 : parseFloat(val) || 0);
                            }}
                            inputProps={{ inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*', style: { textAlign: 'right' } }}
                            disabled={item.itemType === 'Medicine' && !!item.pharmacyId}
                            error={item.itemName !== '' && item.unitPrice <= 0 && item.itemType !== 'Medicine'}
                            helperText={item.itemName !== '' && item.unitPrice <= 0 && item.itemType !== 'Medicine' ? 'Required' : ''}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Disc"
                            value={item.discountPercentage === 0 ? '' : item.discountPercentage}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^0-9.]/g, '');
                              handleBillItemChange(index, 'discountPercentage', val === '' ? 0 : parseFloat(val) || 0);
                            }}
                            inputProps={{ inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*', style: { textAlign: 'right' } }}
                          />
                        </TableCell>
                        <TableCell>
                          {item.itemType === 'Procedure' ? (
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="GST %"
                              value={item.gstPercentage === undefined ? '' : item.gstPercentage}
                              onChange={(e) => {
                                const val = e.target.value.replace(/[^0-9.]/g, '');
                                handleBillItemChange(
                                  index,
                                  'gstPercentage',
                                  val === '' ? 0 : parseFloat(val) || 0,
                                );
                              }}
                              onBlur={(e) => {
                                if (e.target.value === '') {
                                  handleBillItemChange(index, 'gstPercentage', DEFAULT_PROCEDURE_GST);
                                }
                              }}
                              inputProps={{
                                inputMode: 'decimal',
                                pattern: '[0-9]*\\.?[0-9]*',
                                style: { textAlign: 'right' },
                              }}
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{ color: '#9ca3af', textAlign: 'right' }}
                            >
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 'bold',
                              color: '#059669',
                              textAlign: 'right',
                              minHeight: 40,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                            }}
                          >
                            {formatCurrency(
                              (item.quantity || 0) * (item.unitPrice || 0)
                                - (item.discountPercentage || 0)
                                + calculateItemGstAmount(item),
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveBillItem(index)}
                            disabled={billItems.length === 1}
                            sx={{
                              color: billItems.length > 1 ? '#dc2626' : '#ccc',
                              '&:hover': {
                                backgroundColor: billItems.length > 1 ? 'rgba(220, 38, 38, 0.1)' : 'transparent',
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Total Calculation */}
              <Box sx={{ mt: 3, p: 2, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Tax Amount"
                      value={taxAmount === 0 ? '' : taxAmount}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9.]/g, '');
                        setTaxAmount(val === '' ? 0 : parseFloat(val) || 0);
                      }}
                      inputProps={{ inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Discount Amount"
                      value={discountAmount === 0 ? '' : discountAmount}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9.]/g, '');
                        setDiscountAmount(val === '' ? 0 : parseFloat(val) || 0);
                      }}
                      inputProps={{ inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ textAlign: 'right', mt: 2 }}>
                      <Typography variant="h6" sx={{ color: '#2C7A9C', fontWeight: 'bold' }}>
                        Subtotal: {formatCurrency(calculateSubTotal())}
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#6b7280', mt: 0.5 }}>
                        GST (Procedures){getProcedureGstPercentLabel(billItems) && ` @ ${getProcedureGstPercentLabel(billItems)}`}: {formatCurrency(billItems.reduce((sum, item) => sum + calculateItemGstAmount(item), 0))}
                      </Typography>
                      <Typography variant="h5" sx={{ color: '#059669', fontWeight: 'bold', mt: 1 }}>
                        Grand Total: {formatCurrency(calculateTotal())}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Bill Summary */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tax Amount"
                value={taxAmount === 0 ? '' : taxAmount}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9.]/g, '');
                  setTaxAmount(val === '' ? 0 : parseFloat(val) || 0);
                }}
                inputProps={{ inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Discount Amount"
                value={discountAmount === 0 ? '' : discountAmount}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9.]/g, '');
                  setDiscountAmount(val === '' ? 0 : parseFloat(val) || 0);
                }}
                inputProps={{ inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  {paymentMethods.map((method) => (
                    <MenuItem key={method} value={method}>
                      {method}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6">
                Total: {formatCurrency(calculateTotal())}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Draft Preview Section */}
            {showDraftPreview && (
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  p: 2, 
                  borderLeft: { md: '1px solid #e2e8f0' }, 
                  borderTop: { xs: '1px solid #e2e8f0', md: 'none' },
                  backgroundColor: '#f8fafc',
                  height: '100%',
                  minHeight: '600px',
                  overflow: 'auto'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ReceiptIcon sx={{ mr: 1, color: '#2C7A9C' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E5A73' }}>
                        Bill Preview (Draft)
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<PrintIcon />}
                      onClick={printDraftBill}
                      disabled={!selectedPatient || billItems.filter(item => item.itemName && item.quantity > 0).length === 0}
                      sx={{
                        borderColor: '#f59e0b',
                        color: '#d97706',
                        '&:hover': {
                          borderColor: '#d97706',
                          backgroundColor: 'rgba(245, 158, 11, 0.1)',
                        },
                        '&.Mui-disabled': {
                          borderColor: '#e5e7eb',
                          color: '#9ca3af',
                        }
                      }}
                    >
                      Print Draft
                    </Button>
                  </Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This is a preview of how the bill will look. Changes made in the form will update this preview in real-time.
                  </Alert>
                  {renderDraftPreview()}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={handleCloseDialog} sx={{ color: '#6b7280' }}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #047857 0%, #065f46 100%)',
              },
            }}
          >
            Create Bill
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return Item Dialog */}
      <Dialog open={openReturnDialog} onClose={handleCloseReturnDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <UndoIcon />
          Return Item
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 2 }}>
          {selectedBillForReturn && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, color: '#374151' }}>
                Bill: <strong>{selectedBillForReturn.billNumber}</strong> | 
                Patient: <strong>{selectedBillForReturn.patientName}</strong>
              </Typography>

              {/* Select Item to Return */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Item to Return</InputLabel>
                <Select
                  value={selectedItemForReturn?.billItemId || ''}
                  onChange={(e) => {
                    const item = selectedBillForReturn.billItems?.find(i => i.billItemId === e.target.value);
                    setSelectedItemForReturn(item || null);
                    if (item) {
                      setReturnQuantity(1);
                    }
                  }}
                  label="Select Item to Return"
                >
                  {selectedBillForReturn.billItems
                    ?.map((item) => (
                      <MenuItem key={item.billItemId} value={item.billItemId}>
                        {item.itemName} ({item.itemType}) - Qty: {item.quantity} @ {formatCurrency(item.unitPrice)}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              {selectedItemForReturn && (
                <>
                  <TextField
                    fullWidth
                    label="Return Quantity"
                    value={returnQuantity === 0 ? '' : returnQuantity}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      const numVal = val === '' ? 0 : parseInt(val);
                      setReturnQuantity(Math.min(numVal, selectedItemForReturn.quantity));
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || parseInt(e.target.value) < 1) {
                        setReturnQuantity(1);
                      }
                    }}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    sx={{ mb: 2 }}
                    helperText={`Maximum: ${selectedItemForReturn.quantity}`}
                  />

                  <Typography variant="body2" sx={{ mb: 2, p: 2, backgroundColor: '#ecfdf5', borderRadius: 2 }}>
                    Refund Amount: <strong style={{ color: '#059669' }}>
                      {formatCurrency(returnQuantity * selectedItemForReturn.unitPrice)}
                    </strong>
                  </Typography>

                  <TextField
                    fullWidth
                    label="Reason for Return"
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    required
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Additional Notes"
                    value={returnNotes}
                    onChange={(e) => setReturnNotes(e.target.value)}
                    multiline
                    rows={2}
                  />
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseReturnDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmitReturn} 
            variant="contained"
            disabled={!selectedItemForReturn || !returnReason.trim()}
            sx={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
              },
            }}
          >
            Submit Return
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingPage;